"use client";

import { I18nProvider } from "@lingui/react";
import { useRef } from "react";

import { i18n } from "@lingui/core";
import * as messageFR from "./fr/messages.mjs";
import * as messageEN from "./en/messages.mjs";
import * as messageHE from "./he/messages.mjs";
import * as messageIT from "./it/messages.mjs";
import * as messagePT from "./pt/messages.mjs";
import * as messageRU from "./ru/messages.mjs";
import * as messageDE from "./de/messages.mjs";
import * as messageES from "./es/messages.mjs";
import * as messageGR from "./gr/messages.mjs";
import * as messageRO from "./ro/messages.mjs";

import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import 'dayjs/locale/he'
import 'dayjs/locale/it'
import 'dayjs/locale/pt'
import 'dayjs/locale/ru'
import 'dayjs/locale/de'
import 'dayjs/locale/es'
import 'dayjs/locale/ro'
import 'dayjs/locale/el'
import dayjs from "dayjs"
import localizedFormat from "dayjs/plugin/localizedFormat";


const LANGS=[
    {id:"fr",messages:messageFR},
    {id:"en",messages:messageEN},
    {id:"he",messages:messageHE},
    {id:"it",messages:messageIT},
    {id:"pt",messages:messagePT},
    {id:"ru",messages:messageRU},
    {id:"de",messages:messageDE},
    {id:"es",messages:messageES},
    {id:"gr",messages:messageGR},
    {id:"ro",messages:messageRO},
]

// Codes de langue reconnus (source de vérité : LANGS ci-dessus). Exporté pour
// que _document puisse détecter la langue depuis l'URL au SSR sans importer
// tout i18n/index.js (lourd : antd, lodash…).
export const LOCALE_IDS = LANGS.map(l => l.id)

export function getLayoutDirection(lang){
    const excepted=["he"]

    if(excepted.includes(lang)){
        return "rtl"
    }

    return "ltr"
}

export function isRtl(lang){
    return getLayoutDirection(lang)==="rtl"
}

// Singleton plugin setup : dayjs.extend doit être appelé une seule fois.
let dayjsExtended = false;

/**
 * Active la locale Lingui + dayjs si elle n'est pas déjà active.
 * Idempotent — appel multiple sans effet de bord.
 *
 * IMPORTANT : ne jamais re-loader si la locale est déjà active, sinon on
 * passe par un état transitoire où `i18n._` est null (React 18 + Lingui 5),
 * ce qui fait crasher tous les `t``` exécutés pendant ce micro-tick.
 */
function ensureLocale(locale) {
    let langData = LANGS.find(l => l.id === locale) || LANGS[0];

    if (!dayjsExtended) {
        dayjs.extend(localizedFormat);
        dayjsExtended = true;
    }
    if (dayjs.locale() !== (langData.id === "gr" ? "el" : langData.id)) {
        dayjs.locale(langData.id === "gr" ? "el" : langData.id);
    }

    // Si la locale demandée est déjà celle active dans i18n, on ne fait RIEN.
    if (i18n.locale === langData.id) return;

    i18n.loadAndActivate({ locale: langData.id, messages: langData.messages.messages });
}

export function LinguiClientProvider({ children, locale }) {
    // useRef pour exécuter le setup sync au premier render seulement.
    // Pas useEffect : on a besoin que i18n soit prêt AVANT que les enfants
    // ne soient rendus (sinon leurs t`` au premier render crashent).
    const initialized = useRef(false);
    if (!initialized.current) {
        ensureLocale(locale);
        initialized.current = true;
    } else {
        // Sur les renders suivants, on switch seulement si la locale a changé.
        // ensureLocale est idempotent, donc no-op si locale identique.
        ensureLocale(locale);
    }

    return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}
